import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Linkedin, Award, BookOpen } from 'lucide-react';

interface TeamMember {
  id: number;
  name: string;
  title: string;
  company: string;
  profileImage: string;
  bio?: string;
  specializations?: string[];
  qualifications?: string[];
  email?: string;
  type: 'clinical' | 'non-clinical';
}

const TeamMemberPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // This would ideally come from a central data source or API
  const getTeamMember = (memberId: string): TeamMember | null => {
    // For now, returning placeholder data - in production this would fetch from an API or data file
    const allMembers: TeamMember[] = [
      // Clinical Team Members
      {
        id: 1,
        name: "Dr Francis Selemo, HCPC",
        title: "Consultant Psychologist, CBT Therapist, Behavioural Couple Therapy Practitioner",
        company: "HealthCity UK",
        profileImage: require("../assets/selemo-Hteuky_k.png"),
        bio: "Dr Francis Selemo is a highly experienced Consultant Psychologist with expertise in Cognitive Behavioral Therapy and Behavioural Couple Therapy. With years of clinical practice, Dr Selemo specializes in helping individuals and couples overcome mental health challenges.",
        specializations: ["Cognitive Behavioral Therapy", "Couple Therapy", "Clinical Psychology"],
        qualifications: ["HCPC Registered", "Doctorate in Clinical Psychology", "CBT Practitioner Certification"],
        type: 'clinical'
      },
      // Add more members as needed
    ];

    const member = allMembers.find(m => m.id.toString() === memberId);
    if (!member) {
      return {
        id: parseInt(memberId || '0'),
        name: "Team Member",
        title: "Healthcare Professional",
        company: "HealthCity UK",
        profileImage: "",
        bio: "This team member is a dedicated healthcare professional at HealthCity UK, committed to providing excellent care and support to our patients.",
        specializations: ["Patient Care", "Clinical Excellence"],
        qualifications: ["Professional Certification"],
        type: 'clinical'
      };
    }
    return member;
  };

  const member = getTeamMember(id || '1');

  if (!member) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Team Member Not Found</h2>
          <button
            onClick={() => navigate('/teams')}
            className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Teams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/teams')}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Teams
        </button>

        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-green-600 h-32"></div>
          <div className="px-8 pb-8">
            <div className="flex flex-col md:flex-row items-start gap-6 -mt-16">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-200">
                  {member.profileImage ? (
                    <img
                      src={member.profileImage}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-blue-100">
                      <span className="text-4xl text-blue-600">
                        {member.name.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Name and Title */}
              <div className="flex-1 mt-16 md:mt-4">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {member.name}
                </h1>
                <p className="text-lg text-gray-600 mb-4">
                  {member.title}
                </p>
                <div className="flex flex-wrap gap-3">
                  {member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      Contact
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Biography */}
        {member.bio && (
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-blue-600" />
              About
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {member.bio}
            </p>
          </div>
        )}

        {/* Specializations and Qualifications */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Specializations */}
          {member.specializations && member.specializations.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Award className="w-6 h-6 text-green-600" />
                Specializations
              </h2>
              <ul className="space-y-3">
                {member.specializations.map((spec, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">{spec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Qualifications */}
          {member.qualifications && member.qualifications.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Award className="w-6 h-6 text-blue-600" />
                Qualifications
              </h2>
              <ul className="space-y-3">
                {member.qualifications.map((qual, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">{qual}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Contact CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-lg p-8 text-center text-white mt-8">
          <h3 className="text-2xl font-bold mb-4">
            Ready to Book an Appointment?
          </h3>
          <p className="text-lg mb-6 opacity-90">
            Get started with {member.name.split(',')[0]} and begin your journey to better mental health.
          </p>
          <button
            onClick={() => navigate('/book-now')}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberPage;
